import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SubscriptionStatusBadge } from "./SubscriptionStatusBadge";
import type { AdminSubscriptionListItem } from "./types";

interface SubscriptionsTableProps {
  readonly items: readonly AdminSubscriptionListItem[];
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
}

const TH_CLASS =
  "px-3 py-2 text-left font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground";
const TD_CLASS = "px-3 py-3 align-top";

export function SubscriptionsTable({ items }: SubscriptionsTableProps) {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table data-testid="subscriptions-table" className="w-full border-collapse">
        <thead className="border-b border-border bg-muted/30">
          <tr>
            <th className={TH_CLASS}>{t("subscriptions.list.table.property")}</th>
            <th className={TH_CLASS}>{t("subscriptions.list.table.status")}</th>
            <th className={TH_CLASS}>{t("subscriptions.list.table.tier")}</th>
            <th className={TH_CLASS}>{t("subscriptions.list.table.created")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              data-testid={`subscription-row-${item.id}`}
              className="border-b border-border last:border-0 hover:bg-muted/20"
            >
              <td className={TD_CLASS}>
                <Link
                  to={`/subscriptions/${item.id}`}
                  data-testid={`subscription-link-${item.id}`}
                  className="font-sans text-sm font-medium text-primary hover:underline"
                >
                  {item.propertyName}
                </Link>
                <p className="font-sans text-xs text-muted-foreground">{item.ownerEmail}</p>
              </td>
              <td className={TD_CLASS}>
                <SubscriptionStatusBadge
                  status={item.status}
                  testId={`subscription-status-${item.id}`}
                />
              </td>
              <td className={`${TD_CLASS} font-sans text-sm text-foreground`}>{item.tier}</td>
              <td className={`${TD_CLASS} font-sans text-sm text-muted-foreground`}>
                {formatDate(item.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
